/* ──────────────────────────────────────────────────────────────
   src/pages/cliente/ClienteLayout.tsx
   ────────────────────────────────────────────────────────────── */
import { useState } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import {
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Toolbar,
  AppBar,
  Typography,
  CssBaseline,
  Box,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useTranslation } from "react-i18next";
import NotificationBell from "../../components/NotificationBell";
import AccountMenu from "../../components/AccountMenu";
import LanguageSwitch from "../../components/LanguageSwitch";

const drawerWidth = 220;
const collapsedW = 56;

const MENUS = [
  { to: "/cliente/agenda", key: "menu.calendario" },
  { to: "/cliente/historial", key: "menu.historial" },
  { to: "/cliente/cuenta", key: "menu.cuenta" },
];

export default function ClienteLayout() {
  const { t } = useTranslation<"translation">();
  const [open, setOpen] = useState(true);
  const { pathname } = useLocation();

  const toggle = () => setOpen(!open);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <CssBaseline />

      {/* ===== App-Bar superior ===== */}
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Toolbar sx={{ px: 1 }}>
          <IconButton edge="start" onClick={toggle}>
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" sx={{ ml: 2, flexGrow: 1 }}>
            {t("clienteLayout.titulo")}
          </Typography>

          {/* campanita + cuenta */}
          <NotificationBell />
          <AccountMenu />
          <LanguageSwitch />
        </Toolbar>
      </AppBar>

      {/* ===== Drawer lateral ===== */}
      <Drawer
        variant="permanent"
        open={open}
        sx={{
          width: open ? drawerWidth : collapsedW,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: open ? drawerWidth : collapsedW,
            overflowX: "hidden",
            transition: (t) => t.transitions.create("width"),
            boxSizing: "border-box",
            borderRight: "1px solid #e0e0e0",
          },
        }}
      >
        <Toolbar />
        <List disablePadding>
          {MENUS.map((m) => (
            <ListItemButton
              key={m.to}
              component={Link}
              to={m.to}
              selected={pathname.startsWith(m.to)}
              sx={{
                justifyContent: open ? "initial" : "center",
                px: 2.5,
              }}
            >
              <ListItemText primary={t(`clienteLayout.${m.key}`)} sx={{ opacity: open ? 1 : 0 }} />
            </ListItemButton>
          ))}
        </List>
      </Drawer>

      {/* ===== Contenido principal ===== */}
      <Box component="main" sx={{ flexGrow: 1, p: 3, mt: "64px" }}>
        <Outlet />
      </Box>
    </Box>
  );
}
